import { getDistanceKm } from '../utils';

/**
 * Type for a coordinate pair [latitude, longitude]
 */
type Coordinate = [number, number];

/**
 * Generate a grid of points within a radius of a center point.
 * Uses an adaptive grid spacing based on the radius size and generates points in a circular pattern
 * to optimize coverage and reduce the number of points needed.
 * 
 * @param center - The center coordinate [lat, lng]
 * @param radiusKm - The radius in kilometers
 * @returns Array of coordinates [lat, lng][]
 */
export function generateGridPoints(center: Coordinate, radiusKm: number): Coordinate[] {
    const points: Coordinate[] = [];
    const [lat0, lng0] = center;
    
    // Adaptive grid spacing based on radius size
    // For larger areas, use less dense grid to reduce API calls
    const baseSpacingKm = 0.5;
    const adaptiveSpacing = Math.max(baseSpacingKm, radiusKm / 20);
    
    console.log(`Using adaptive grid spacing of ${adaptiveSpacing.toFixed(2)}km for ${radiusKm}km radius`);
    
    // Convert km spacing to lat/lng degrees
    const dLat = adaptiveSpacing / 111.32; // 1 degree lat ≈ 111.32 km
    const dLng = adaptiveSpacing / (111.32 * Math.cos(lat0 * Math.PI / 180)); // Adjust for longitude compression
    
    // Optimization: Sample points in a circular pattern to reduce number of points
    // and focus more points near the center where changes are more significant
    const rings = Math.ceil(radiusKm / adaptiveSpacing);
    
    // Add center point
    points.push([lat0, lng0]);
    
    // Add rings of points
    for (let r = 1; r <= rings; r++) {
        const ringRadius = r * adaptiveSpacing;
        const circumference = 2 * Math.PI * ringRadius;
        const numPoints = Math.max(8, Math.floor(circumference / adaptiveSpacing));
        
        for (let i = 0; i < numPoints; i++) {
            const angle = (i / numPoints) * 2 * Math.PI;
            const dx = ringRadius * Math.sin(angle);
            const dy = ringRadius * Math.cos(angle);
            
            const lat = lat0 + dy * dLat;
            const lng = lng0 + dx * dLng;
            
            // Only add points within the specified radius
            const dist = getDistanceKm([lat0, lng0], [lat, lng]);
            if (dist <= radiusKm) {
                points.push([lat, lng]);
            }
        }
    }
    
    console.log(`Generated ${points.length} grid points for ${radiusKm}km radius`);
    return points;
}

/**
 * Get travel times for a grid of points using the Google Routing API
 * @param gridPoints - Array of coordinates to calculate travel times for
 * @param destination - The destination coordinate
 * @param arrivalTime - The target arrival time
 * @param transitTimeCache - Cache for storing travel times
 * @returns Array of travel times in minutes
 */
export async function getTravelTimesForGrid(
    gridPoints: Coordinate[],
    destination: Coordinate,
    arrivalTime: Date,
    transitTimeCache: Map<string, number>
): Promise<number[]> {
    // Import batchCallGoogleRoutingAPI dynamically to avoid circular dependency
    const { batchCallGoogleRoutingAPI } = await import('./routing');
    
    return batchCallGoogleRoutingAPI(
        gridPoints.map(point => ({
            origin: point,
            destination
        })),
        arrivalTime,
        transitTimeCache
    );
}

/**
 * Calculate the optimal grid spacing based on the radius and desired point density
 * @param radiusKm - Radius in kilometers
 * @param targetPoints - Target number of points (default 100)
 * @returns Optimal spacing in kilometers
 */
export function calculateOptimalGridSpacing(radiusKm: number, targetPoints: number = 100): number {
    // Area of the circle
    const area = Math.PI * radiusKm * radiusKm;
    
    // Approximate spacing needed to get targetPoints in the area
    // Each grid cell is roughly spacing² in area
    const spacing = Math.sqrt(area / targetPoints);
    
    // Apply minimum and maximum constraints
    const minSpacing = 0.25; // 250m minimum
    const maxSpacing = radiusKm / 10; // At least 10 points across diameter
    
    return Math.max(minSpacing, Math.min(maxSpacing, spacing));
} 